import { GOOGLE_CONFIG } from '../config';

class GmailService {
  constructor() {
    this.gapi = null;
    this.tokenClient = null;
    this.accessToken = null;
    this.tokenExpiry = null;
    this.isInitialized = false;
    this.refreshTimer = null;

    // Try to restore token from localStorage on initialization
    this.restoreToken();
  }

  // Save token to localStorage and schedule refresh
  saveToken(accessToken, expiresIn) {
    this.accessToken = accessToken;
    this.tokenExpiry = Date.now() + (expiresIn * 1000); // expiresIn is in seconds

    localStorage.setItem('gmail_access_token', accessToken);
    localStorage.setItem('gmail_token_expiry', this.tokenExpiry.toString());

    // Schedule automatic refresh before token expires
    this.scheduleTokenRefresh(expiresIn);
  }

  // Schedule automatic token refresh
  scheduleTokenRefresh(expiresIn) {
    // Clear any existing refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Refresh 5 minutes before expiry
    const refreshTime = (expiresIn - 300) * 1000; // Convert to ms, subtract 5 minutes

    if (refreshTime > 0) {
      console.log('Token refresh scheduled in', Math.round(refreshTime / 1000 / 60), 'minutes');
      this.refreshTimer = setTimeout(() => {
        console.log('Auto-refreshing token...');
        this.refreshToken();
      }, refreshTime);
    }
  }

  // Refresh the access token
  async refreshToken() {
    if (!this.tokenClient || !this.isInitialized) {
      console.log('Cannot refresh: not initialized');
      return false;
    }

    return new Promise((resolve) => {
      this.tokenClient.callback = (response) => {
        if (response.error) {
          console.error('Token refresh failed:', response);
          this.clearToken();
          resolve(false);
          return;
        }
        const expiresIn = response.expires_in || 3600;
        this.saveToken(response.access_token, expiresIn);
        this.gapi.client.setToken({ access_token: this.accessToken });
        console.log('Token refreshed successfully');
        resolve(true);
      };

      // Request new token silently (no prompt)
      this.tokenClient.requestAccessToken({ prompt: '' });
    });
  }

  // Restore token from localStorage
  restoreToken() {
    const token = localStorage.getItem('gmail_access_token');
    const expiry = localStorage.getItem('gmail_token_expiry');

    if (token && expiry) {
      const expiryTime = parseInt(expiry, 10);
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      // Check if token is still valid (with 5 minute buffer)
      if (timeUntilExpiry > (5 * 60 * 1000)) {
        this.accessToken = token;
        this.tokenExpiry = expiryTime;
        console.log('Token restored from localStorage. Expires in', Math.round(timeUntilExpiry / 1000 / 60), 'minutes');

        // Schedule refresh for restored token
        const expiresInSeconds = Math.floor(timeUntilExpiry / 1000);
        this.scheduleTokenRefresh(expiresInSeconds);

        return true;
      } else {
        // Token expired, clear it
        console.log('Stored token expired, clearing...');
        this.clearToken();
      }
    } else {
      console.log('No stored token found');
    }
    return false;
  }

  // Clear token from memory and localStorage
  clearToken() {
    this.accessToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem('gmail_access_token');
    localStorage.removeItem('gmail_token_expiry');
  }

  // Initialize the Gmail API with new Google Identity Services
  async init() {
    return new Promise((resolve, reject) => {
      // Load the Google API client library
      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.onload = () => {
        window.gapi.load('client', async () => {
          try {
            console.log('Initializing Gmail API client...');
            await window.gapi.client.init({
              apiKey: GOOGLE_CONFIG.apiKey,
              discoveryDocs: GOOGLE_CONFIG.discoveryDocs,
            });
            this.gapi = window.gapi;

            // Load the Google Identity Services library
            const gisScript = document.createElement('script');
            gisScript.src = 'https://accounts.google.com/gsi/client';
            gisScript.onload = () => {
              // Initialize the token client for OAuth
              this.tokenClient = window.google.accounts.oauth2.initTokenClient({
                client_id: GOOGLE_CONFIG.clientId,
                scope: GOOGLE_CONFIG.scope,
                callback: (response) => {
                  if (response.error) {
                    console.error('Token error:', response);
                    return;
                  }
                  // Save token with expiry (default to 3600 seconds if not provided)
                  const expiresIn = response.expires_in || 3600;
                  this.saveToken(response.access_token, expiresIn);
                  this.gapi.client.setToken({ access_token: this.accessToken });
                }
              });

              this.isInitialized = true;

              // If we have a restored token, set it in the API client
              if (this.accessToken) {
                try {
                  this.gapi.client.setToken({ access_token: this.accessToken });
                  console.log('Gmail API initialized with restored token');
                  console.log('Token expiry:', new Date(this.tokenExpiry).toLocaleString());
                } catch (e) {
                  console.error('Error setting restored token:', e);
                  this.clearToken();
                }
              } else {
                console.log('Gmail API initialized successfully (no saved token)');
              }

              resolve();
            };
            gisScript.onerror = reject;
            document.body.appendChild(gisScript);
          } catch (error) {
            console.error('Gmail API initialization error:', error);
            reject(error);
          }
        });
      };
      gapiScript.onerror = reject;
      document.body.appendChild(gapiScript);
    });
  }

  // Sign in to Google
  async signIn() {
    if (!this.isInitialized) {
      throw new Error('Gmail API not initialized');
    }

    return new Promise((resolve, reject) => {
      try {
        // Request an access token
        this.tokenClient.callback = (response) => {
          if (response.error) {
            reject(response);
            return;
          }
          // Save token with expiry
          const expiresIn = response.expires_in || 3600;
          this.saveToken(response.access_token, expiresIn);
          this.gapi.client.setToken({ access_token: this.accessToken });
          resolve();
        };

        // Use prompt: '' to avoid forcing consent screen every time
        // Will only prompt if necessary (new scopes, expired token, etc)
        this.tokenClient.requestAccessToken({ prompt: '' });
      } catch (error) {
        reject(error);
      }
    });
  }

  // Sign out from Google
  async signOut() {
    if (!this.isInitialized) {
      throw new Error('Gmail API not initialized');
    }

    if (this.accessToken) {
      window.google.accounts.oauth2.revoke(this.accessToken, () => {
        console.log('Access token revoked');
      });
      this.clearToken();
      this.gapi.client.setToken(null);
    }
  }

  // Check if user is signed in
  isSignedIn() {
    return !!this.accessToken;
  }

  // Check if token is valid and refresh if needed
  async ensureValidToken() {
    if (!this.accessToken) {
      throw new Error('Not authenticated');
    }

    if (!this.tokenExpiry) {
      return true; // No expiry info, assume valid
    }

    const timeUntilExpiry = this.tokenExpiry - Date.now();

    // If token expires in less than 5 minutes, refresh it
    if (timeUntilExpiry < (5 * 60 * 1000)) {
      console.log('Token expiring soon, refreshing...');
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        throw new Error('Session expired. Please sign in again.');
      }
    }

    return true;
  }

  // Get all message IDs
  async getAllMessageIds(pageToken = null, allIds = []) {
    // Ensure token is valid before making API call
    await this.ensureValidToken();

    const response = await this.gapi.client.gmail.users.messages.list({
      userId: 'me',
      maxResults: 500,
      pageToken: pageToken
    });

    const messages = response.result.messages || [];
    allIds.push(...messages.map(m => m.id));

    if (response.result.nextPageToken) {
      return this.getAllMessageIds(response.result.nextPageToken, allIds);
    }

    return allIds;
  }

  // Get all message IDs from a specific sender
  async getMessageIdsFromSender(senderEmail, pageToken = null, allIds = []) {
    const response = await this.gapi.client.gmail.users.messages.list({
      userId: 'me',
      q: `from:${senderEmail}`,
      maxResults: 500,
      pageToken: pageToken
    });

    const messages = response.result.messages || [];
    allIds.push(...messages.map(m => m.id));

    if (response.result.nextPageToken) {
      return this.getMessageIdsFromSender(senderEmail, response.result.nextPageToken, allIds);
    }

    return allIds;
  }

  // Delete a single message (permanently deletes, bypassing trash)
  async deleteMessage(messageId) {
    try {
      await this.gapi.client.gmail.users.messages.delete({
        userId: 'me',
        id: messageId
      });
      return true;
    } catch (error) {
      // 404 means message already deleted - count as success
      if (error.status === 404) {
        return true;
      }
      console.error(`Error deleting message ${messageId}:`, error);
      return false;
    }
  }

  // Trash a single message (moves to trash instead of permanent delete)
  async trashMessage(messageId) {
    try {
      await this.gapi.client.gmail.users.messages.trash({
        userId: 'me',
        id: messageId
      });
      return true;
    } catch (error) {
      console.error(`Error trashing message ${messageId}:`, error);
      return false;
    }
  }

  // Delete all emails from a specific sender
  async deleteAllEmailsFromSender(senderEmail, onProgress) {
    try {
      // Get all message IDs from this sender
      const messageIds = await this.getMessageIdsFromSender(senderEmail);
      const total = messageIds.length;
      let deleted = 0;
      let failed = 0;
      const delayBetweenDeletes = 100; // 100ms delay to avoid rate limits

      if (total === 0) {
        return { total: 0, deleted: 0, failed: 0 };
      }

      // Delete messages one by one
      for (const messageId of messageIds) {
        try {
          const success = await this.deleteMessage(messageId);
          if (success) {
            deleted++;
          } else {
            failed++;
          }

          if (onProgress) {
            onProgress(deleted + failed, total);
          }

          // Add delay to avoid rate limiting
          if (deleted + failed < total) {
            await this.delay(delayBetweenDeletes);
          }
        } catch (error) {
          console.error(`Failed to delete message ${messageId}:`, error);
          failed++;
        }
      }

      return { total, deleted, failed };
    } catch (error) {
      console.error(`Error deleting emails from ${senderEmail}:`, error);
      throw error;
    }
  }

  // Helper function to add delay between requests
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get message headers in batches with rate limiting
  async getMessageHeaders(messageIds, onProgress) {
    const senders = new Map();
    const batchSize = 75; // Process 75 messages per batch (stay under 15000/min quota = 250/sec)
    const delayBetweenBatches = 1200; // 1200ms delay between batches

    for (let i = 0; i < messageIds.length; i += batchSize) {
      const batch = messageIds.slice(i, i + batchSize);

      // Process messages in smaller parallel groups
      const promises = batch.map(async (id, index) => {
        // Stagger requests within batch to avoid rate limiting
        await this.delay(index * 15); // 15ms delay between each request in batch

        try {
          return await this.gapi.client.gmail.users.messages.get({
            userId: 'me',
            id: id,
            format: 'metadata',
            metadataHeaders: ['From']
          });
        } catch (err) {
          console.error(`Error fetching message ${id}:`, err);
          return null;
        }
      });

      const results = await Promise.all(promises);

      results.forEach(result => {
        if (result && result.result) {
          const headers = result.result.payload.headers;
          const fromHeader = headers.find(h => h.name === 'From');
          if (fromHeader) {
            const sender = this.extractEmail(fromHeader.value);
            const displayName = this.extractDisplayName(fromHeader.value);

            if (sender) {
              if (!senders.has(sender)) {
                senders.set(sender, {
                  email: sender,
                  displayName: displayName,
                  count: 0
                });
              }
              senders.get(sender).count++;
            }
          }
        }
      });

      if (onProgress) {
        onProgress(Math.min(i + batchSize, messageIds.length), messageIds.length);
      }

      // Wait between batches to avoid rate limiting
      if (i + batchSize < messageIds.length) {
        await this.delay(delayBetweenBatches);
      }
    }

    return Array.from(senders.values()).sort((a, b) => b.count - a.count);
  }

  // Extract email from "Name <email@example.com>" format
  extractEmail(fromString) {
    const emailMatch = fromString.match(/<(.+?)>/);
    if (emailMatch) {
      return emailMatch[1].toLowerCase();
    }
    return fromString.toLowerCase();
  }

  // Extract display name from "Name <email@example.com>" format
  extractDisplayName(fromString) {
    const nameMatch = fromString.match(/^(.+?)\s*</);
    if (nameMatch) {
      return nameMatch[1].replace(/^["']|["']$/g, '');
    }
    return fromString;
  }

  // Create a label
  async createLabel(labelName) {
    try {
      const response = await this.gapi.client.gmail.users.labels.create({
        userId: 'me',
        resource: {
          name: labelName,
          labelListVisibility: 'labelShow',
          messageListVisibility: 'show'
        }
      });
      return response.result.id;
    } catch (error) {
      // If label already exists, find it
      if (error.status === 409) {
        const labels = await this.gapi.client.gmail.users.labels.list({
          userId: 'me'
        });
        const label = labels.result.labels.find(l => l.name === labelName);
        return label ? label.id : null;
      }
      throw error;
    }
  }

  // Create filter for a sender
  async createFilter(senderEmail, labelId) {
    try {
      await this.gapi.client.gmail.users.settings.filters.create({
        userId: 'me',
        resource: {
          criteria: {
            from: senderEmail
          },
          action: {
            addLabelIds: [labelId],
            removeLabelIds: ['INBOX']
          }
        }
      });
      return true;
    } catch (error) {
      // 400 FAILED_PRECONDITION means filter already exists - count as success
      if (error.status === 400 && error.result?.error?.status === 'FAILED_PRECONDITION') {
        return true;
      }
      console.error(`Error creating filter for ${senderEmail}:`, error);
      return false;
    }
  }

  // Create filters for all non-selected senders
  async createFiltersForSenders(senderEmails, onProgress) {
    const labelId = await this.createLabel('Not Selected');
    let processed = 0;

    for (const email of senderEmails) {
      await this.createFilter(email, labelId);
      processed++;
      if (onProgress) {
        onProgress(processed, senderEmails.length);
      }
    }

    return { labelId, processed };
  }

  // Get all existing filters
  async getAllFilters() {
    try {
      const response = await this.gapi.client.gmail.users.settings.filters.list({
        userId: 'me'
      });
      return response.result.filter || [];
    } catch (error) {
      console.error('Error fetching filters:', error);
      return [];
    }
  }

  // Find filter ID for a specific sender
  async findFilterForSender(senderEmail) {
    const filters = await this.getAllFilters();
    const filter = filters.find(f =>
      f.criteria && f.criteria.from && f.criteria.from.toLowerCase() === senderEmail.toLowerCase()
    );
    return filter ? filter.id : null;
  }

  // Delete a filter by ID
  async deleteFilter(filterId) {
    try {
      await this.gapi.client.gmail.users.settings.filters.delete({
        userId: 'me',
        id: filterId
      });
      return true;
    } catch (error) {
      console.error(`Error deleting filter ${filterId}:`, error);
      return false;
    }
  }

  // Remove filter for a sender (when added back to selected list)
  async removeFilterForSender(senderEmail) {
    const filterId = await this.findFilterForSender(senderEmail);
    if (filterId) {
      return await this.deleteFilter(filterId);
    }
    return false;
  }

  // Delete all filters with progress callback and rate limiting
  async deleteAllFilters(onProgress) {
    try {
      const filters = await this.getAllFilters();
      const total = filters.length;
      let deleted = 0;
      const delayBetweenDeletes = 200; // 200ms delay between deletions to avoid rate limits

      for (const filter of filters) {
        try {
          await this.deleteFilter(filter.id);
          deleted++;
          if (onProgress) {
            onProgress(deleted, total);
          }

          // Add delay between deletions to avoid rate limiting
          if (deleted < total) {
            await this.delay(delayBetweenDeletes);
          }
        } catch (error) {
          console.error(`Failed to delete filter ${filter.id}:`, error);
        }
      }

      return { total, deleted };
    } catch (error) {
      console.error('Error deleting all filters:', error);
      throw error;
    }
  }

  // Sync filters based on selection changes
  async syncFilters(previouslySelected, currentlySelected, onProgress) {
    const labelId = await this.createLabel('Not Selected');

    // Find newly selected senders (need to remove filters)
    const newlySelected = Array.from(currentlySelected).filter(
      email => !previouslySelected.has(email)
    );

    // Find newly unselected senders (need to create filters)
    const newlyUnselected = Array.from(previouslySelected).filter(
      email => !currentlySelected.has(email)
    );

    let processed = 0;
    const total = newlySelected.length + newlyUnselected.length;

    // Remove filters for newly selected senders
    for (const email of newlySelected) {
      await this.removeFilterForSender(email);
      processed++;
      if (onProgress) {
        onProgress(processed, total, 'removing');
      }
    }

    // Create filters for newly unselected senders
    for (const email of newlyUnselected) {
      await this.createFilter(email, labelId);
      processed++;
      if (onProgress) {
        onProgress(processed, total, 'creating');
      }
    }

    return {
      removed: newlySelected.length,
      created: newlyUnselected.length,
      total: processed
    };
  }
}

export default new GmailService();
